import React, { useState } from "react";
import { api, formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Store } from "lucide-react";

const CATS = [
  { k: "rudraksha", l: "🪬 Rudraksha / Mala / Spiritual Beads" },
  { k: "ghee", l: "🪔 Ghee Diyas / Oil Lamps" },
  { k: "incense", l: "🧴 Incense / Dhoop / Sandalwood" },
  { k: "books", l: "📚 Spiritual Books / Scripture Media" },
  { k: "idols", l: "🕉 Idols / Yantras / Puja Items" },
  { k: "other", l: "✨ Other sacred supplies" },
];

export default function SupplierApply() {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    country: "India", category: "rudraksha", description: "", website: "", monthly_capacity: 100, password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/suppliers/apply", { ...form, monthly_capacity: parseInt(form.monthly_capacity) || null });
      setDone(true);
      toast.success("Application received — admin review within 48 hours");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="supplier-success" className="max-w-xl mx-auto px-4 py-20 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#2F7D32] mx-auto" />
        <h1 className="font-serif text-4xl text-[#4A0E17] mt-5">॥ धन्यवाद ॥</h1>
        <p className="text-[#63534E] mt-4">
          Your supplier application is in. Our team will reach out within 48 hours to verify authenticity, align on pricing, and onboard your products to the marketplace.
        </p>
        <p className="text-[#63534E] mt-3 text-sm">Once you're approved, <a href="/login" className="text-[#E65100] font-semibold">sign in</a> with your email and password to upload products from your Supplier Dashboard.</p>
      </div>
    );
  }

  return (
    <div data-testid="supplier-apply" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <Store className="w-10 h-10 text-[#E65100]" />
      <div className="overline mt-4">Sell on Pancham Dham</div>
      <h1 className="font-serif text-5xl text-[#4A0E17] mt-2 leading-tight">Vendor & Supplier Onboarding</h1>
      <p className="text-[#63534E] mt-5 text-lg leading-relaxed">
        Rudraksha growers, ghee producers, incense makers, book publishers and idol artisans — reach devotees worldwide and earn <strong className="text-[#E65100]">up to 85%</strong> of every sale. We handle checkout, email receipts, and customer discovery.
      </p>

      <div className="mt-8 bg-[#F3E5AB]/40 border border-[#D4AF37]/40 rounded-xl p-5 text-sm text-[#4A0E17]">
        <strong>Platform fee:</strong> 15% success fee on each sale (negotiable at scale). We never charge upfront — only when you earn.
      </div>

      <form onSubmit={submit} className="mt-8 space-y-5 bg-[#FFFDF9] sacred-border rounded-3xl p-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="s-business" required placeholder="Business name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="s-contact" required placeholder="Your name (contact)" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="s-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="s-phone" placeholder="Phone / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="s-country" required placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="s-website" placeholder="Website (optional)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="s-password" required type="password" minLength={6} placeholder="Create a password (for your supplier dashboard)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11 sm:col-span-2" />
        </div>
        <div>
          <label className="overline">Primary category</label>
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            {CATS.map((c) => (
              <button type="button" key={c.k} data-testid={`sup-cat-${c.k}`} onClick={() => setForm({ ...form, category: c.k })}
                className={`text-left px-4 h-11 rounded-xl text-sm transition-sacred border ${form.category === c.k ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-white text-[#4A0E17] border-[#D4AF37]/40"}`}>
                {c.l}
              </button>
            ))}
          </div>
        </div>
        <Textarea data-testid="s-description" required rows={4} placeholder="Tell us about your products, sourcing, authenticity certifications, how long you've been in business…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white border-[#D4AF37]/40" />
        <Input data-testid="s-capacity" type="number" min="1" placeholder="Monthly fulfillment capacity (units)" value={form.monthly_capacity} onChange={(e) => setForm({ ...form, monthly_capacity: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
        <Button data-testid="s-submit" type="submit" disabled={submitting} className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-12 text-base">
          {submitting ? "Submitting…" : "Submit application"}
        </Button>
      </form>
    </div>
  );
}
